import axios, { AxiosResponse } from "axios";
import { getStoredAuthToken } from "./authToken";
import { objectToQueryString } from "./url";
import { apiEndpoints } from "./apiEndpoints";
import SystemPayRoutes from "./Routes";
import { STATUS_CODE } from "./auth";

interface ApiError {
  code: string;
  message: string;
  status: number;
  data: Record<string, any>;
}

interface OptimisticUpdateParams<T> {
  updatedFields: T;
  currentFields: T;
  setLocalData: (data: T) => void;
}

// Constrain the type T to match the expected format for variables
type ApiVariables = Record<string, any>;

const defaults = {
  baseURL: process.env.REACT_APP_API_BASE_URL || "http://localhost:3000/api",
  error: {
    code: "INTERNAL_ERROR",
    message: "Something went wrong. Please check your internet connection or contact our support.",
    status: 503,
    data: {},
  } as ApiError,
};

export const getStoredRefreshToken = () => localStorage.getItem("refreshToken");

export const setAuthToken = (token: string) => localStorage.setItem("authToken", token);
export const setRefreshToken = (token: string) => localStorage.setItem("refreshToken", token);

export const refreshAccessToken = async (): Promise<string | null> => {
  try {
    const refreshToken = getStoredRefreshToken();
    if (!refreshToken) return null;

    const response = await axios.get(`${defaults.baseURL}${apiEndpoints.refreshToken}`, {
      headers: {
        Authorization: `Bearer ${refreshToken}`,
      },
    });
    const newToken = response?.data?.data?.accessToken ?? "";
    setAuthToken(newToken);
    return newToken;
  } catch (error) {
    console.error("Failed to refresh token", error);
    localStorage.clear(); // Clear tokens and force login
    return null;
  }
};

// Function to get headers
const getHeaders = async (refreshToken?: string): Promise<Record<string, string | undefined>> => {
  const token = await getStoredAuthToken();
  return {
    "Content-Type": "application/json",
    Authorization: refreshToken ? `Bearer ${refreshToken}` : token ? `Bearer ${token}` : undefined,
  };
};

const api = async <T>(
  method: "get" | "post" | "put" | "patch" | "delete",
  url: string,
  options?: {
    params?: ApiVariables; // Optional params for query strings
    data?: ApiVariables;   // Optional payload for non-GET requests
  }
): Promise<T> => {
  let headers = await getHeaders();

  try {
    const response: AxiosResponse<T> = await axios({
      url: `${defaults.baseURL}${url}`,
      method,
      headers,
      params: options?.params,
      data: options?.data,
      paramsSerializer: objectToQueryString,
    });
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error?.response) {
      // Check if the error is due to an expired token
      if (error?.response?.status === STATUS_CODE.BAD_REQUEST) {
        throw { message: error?.response?.data?.message };
      } else if (error.response.status === STATUS_CODE.UNAUTHORIZED) {
        const newToken = await refreshAccessToken();
        if (newToken) {
          headers = await getHeaders(newToken); // Get updated headers
          try {
            // Retry the original request with new token
            const retryResponse: AxiosResponse<T> = await axios({
              url: `${defaults.baseURL}${url}`,
              method,
              headers,
              params: options?.params,
              data: options?.data,
              paramsSerializer: objectToQueryString,
            });
            return retryResponse.data;
          } catch (retryError) {
            console.error("Retry failed", retryError);
            throw defaults.error;
          }
        } else {
          localStorage.clear(); // Clear session if refresh fails
          window.location.href = SystemPayRoutes.signIn;
          throw { message: "Session expired. Please log in again." };
        }
      }

      if (error.response.status === 402) {
        console.error("402 - Organization is out of tokens");
      }

      if (error?.response?.data?.message && error?.response?.status !== 503 && error?.response?.status !== 401) {
        throw { message: error?.response?.data?.message };
      }
    }

    throw defaults.error;
  }
};

const optimisticUpdate = async <T extends ApiVariables>(
  url: string,
  { updatedFields, currentFields, setLocalData }: OptimisticUpdateParams<T>
): Promise<void> => {
  try {
    setLocalData(updatedFields);
    await api<T>("put", url, { data: updatedFields });
  } catch (error) {
    setLocalData(currentFields);
    // Handle the error, e.g., show a notification
    console.error((error as ApiError).message);
  }
};

export default {
  get: <T>(url: string, options?: { params?: ApiVariables }): Promise<T> =>
    api<T>("get", url, options),
  post: <T>(url: string, options?: { data?: ApiVariables }): Promise<T> =>
    api<T>("post", url, options),
  put: <T>(url: string, options?: { data?: ApiVariables }): Promise<T> =>
    api<T>("put", url, options),
  patch: <T>(url: string, options?: { data?: ApiVariables }): Promise<T> =>
    api<T>("patch", url, options),
  delete: <T>(url: string, options?: { params?: ApiVariables }): Promise<T> =>
    api<T>("delete", url, options),
  optimisticUpdate,
};


const apiWithFormData = async <T>(
  method: "post" | "put" | "patch",
  url: string,
  formData: FormData
): Promise<T> => {
  let headers = await getHeaders();
  headers["Content-Type"] = "multipart/form-data";

  try {
    const response: AxiosResponse<T> = await axios({
      url: `${defaults.baseURL}${url}`,
      method,
      headers,
      data: formData,
    });
    return response.data;
  } catch (error: any) {
    console.error("API call error", error);
    if (axios.isAxiosError(error) && error?.response) {
      if (error?.response?.status === 401) {
        const newToken = await refreshAccessToken();
        if (newToken) {
          headers = await getHeaders(newToken); // Get new headers with refreshed token
          try {
            // Retry the request with the new token
            const retryResponse: AxiosResponse<T> = await axios({
              url: `${defaults.baseURL}${url}`,
              method,
              headers,
              data: formData,
            });
            return retryResponse.data;
          } catch (retryError) {
            console.error("Retry failed", retryError);
            throw defaults.error;
          }
        } else {
          // Refresh failed too (e.g. backend rejected refresh token after a password
          // change in another browser). Mirror the GET-path behavior — clear local
          // state and bounce to sign-in. Without this redirect the FormData uploads
          // path would silently fail forever.
          localStorage.clear();
          if (typeof window !== 'undefined') {
            window.location.href = SystemPayRoutes.signIn;
          }
          throw { message: "Session expired. Please log in again." };
        }
      }

      if (error.response.status === 402) {
        console.error("402 - Organization is out of tokens");
      }

      if (error?.response?.data?.Error && error?.response?.status !== 503 && error?.response?.status !== 401) {
        throw { message: error?.response?.data?.Error };
      }
    }

    throw error?.response?.data || defaults.error;
  }
};


const apiWithFormDataMethods = {
  post: <T>(url: string, formData: FormData): Promise<T> =>
    apiWithFormData<T>("post", url, formData),
  put: <T>(url: string, formData: FormData): Promise<T> =>
    apiWithFormData<T>("put", url, formData),
  patch: <T>(url: string, formData: FormData): Promise<T> =>
    apiWithFormData<T>("patch", url, formData),
};

export { apiWithFormDataMethods };